import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Sparkle, PhoneCall } from "@phosphor-icons/react";

/**
 * Listens for the `nc:plan-limit` global event fired by our axios interceptor
 * whenever the backend returns 402 (quota exceeded or feature not in plan).
 * Renders a blocking modal with a "Book a call" CTA that opens /book-a-call
 * (Calendly-embedded page) — since Stripe UI is off, upgrades are manual.
 */
export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const onLimit = (e) => {
      setMessage(e.detail?.message || "You've hit your plan limit. Upgrade to continue.");
      setOpen(true);
    };
    window.addEventListener("nc:plan-limit", onLimit);
    return () => window.removeEventListener("nc:plan-limit", onLimit);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      data-testid="upgrade-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg border relative"
        style={{ background: "var(--surface-1)", borderColor: "var(--accent)" }}
      >
        <button
          onClick={() => setOpen(false)}
          data-testid="upgrade-modal-close"
          className="absolute top-3 right-3 p-2 hover:bg-[#15303A]"
          aria-label="Close"
        >
          <X weight="bold" />
        </button>
        <div className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={22} weight="fill" color="var(--accent)" />
            <div className="overline" style={{ color: "var(--accent)" }}>// upgrade required</div>
          </div>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-3">
            You&apos;ve hit your plan&apos;s ceiling
          </h2>
          <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
            {message}
          </p>
          <div className="border p-4 mb-6" style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
            <div className="overline mb-2">// next step</div>
            <p className="text-sm">
              Book a 15-minute call — we&apos;ll get you invoiced and upgraded the same day, no card required until you approve the plan.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/book-a-call"
              onClick={() => setOpen(false)}
              data-testid="upgrade-book-call-btn"
              className="btn-primary flex items-center justify-center gap-2 flex-1"
            >
              <PhoneCall weight="bold" /> Book a call to upgrade
            </Link>
            <button
              onClick={() => setOpen(false)}
              data-testid="upgrade-later-btn"
              className="btn-secondary"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
